import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { AlertTriangle, ShieldAlert, Info } from "lucide-react";
import Button from "./Button";

/**
 * ─── Confirm ──────────────────────────────────────────────────────────────
 * Programmatic confirmation dialog. Replaces every window.confirm().
 *
 * Usage
 *   // 1. Wrap the app tree (once, at OwnerLayout level):
 *        <ConfirmProvider>...</ConfirmProvider>
 *
 *   // 2. In any component:
 *        const confirm = useConfirm();
 *        const ok = await confirm({
 *          title: "Delete organization?",
 *          description: "This will permanently remove Acme Inc. and all its data.",
 *          confirmLabel: "Delete",
 *          tone: "danger",
 *        });
 *        if (ok) await api.delete(...)
 *
 * Options
 *   title           string
 *   description     string | ReactNode
 *   confirmLabel    string       (default "Confirm")
 *   cancelLabel     string       (default "Cancel")
 *   tone            "danger" | "warning" | "info"  (default "danger")
 *   icon            ReactNode    override
 *
 * Focus: Cancel is focused by default. Prevents accidental confirmation.
 * Keyboard: Esc cancels; Enter confirms only when the confirm button has focus.
 */

const ConfirmContext = createContext(null);

const ICONS = {
  danger: ShieldAlert, warning: AlertTriangle, info: Info,
};

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);
  const resolverRef = useRef(null);
  const cancelBtnRef = useRef(null);

  const confirm = useCallback((opts = {}) => new Promise(resolve => {
    resolverRef.current = resolve;
    setState({
      title: "Are you sure?",
      confirmLabel: "Confirm",
      cancelLabel: "Cancel",
      tone: "danger",
      ...opts,
    });
    // focus Cancel next tick
    setTimeout(() => cancelBtnRef.current?.focus(), 30);
  }), []);

  const close = useCallback((result) => {
    resolverRef.current?.(result);
    resolverRef.current = null;
    setState(null);
  }, []);

  const dialog = state && ReactDOM.createPortal(
    <div className="ds-confirm-scrim" role="dialog" aria-modal="true"
         aria-labelledby="ds-confirm-title"
         onMouseDown={(e) => { if (e.target === e.currentTarget) close(false); }}
         onKeyDown={(e) => { if (e.key === "Escape") close(false); }}>
      <div className={`ds-confirm ds-confirm--${state.tone}`} data-testid="ds-confirm-dialog">
        <div className="ds-confirm__icon" aria-hidden>
          {state.icon || (() => { const I = ICONS[state.tone] || AlertTriangle; return <I size={18} strokeWidth={2.2} />; })()}
        </div>
        <div id="ds-confirm-title" className="ds-confirm__title">{state.title}</div>
        {state.description && <div className="ds-confirm__desc">{state.description}</div>}
        <div className="ds-confirm__actions">
          <Button ref={cancelBtnRef} variant="ghost"
                  onClick={() => close(false)} testId="ds-confirm-cancel">
            {state.cancelLabel}
          </Button>
          <Button
            variant={state.tone === "danger" ? "danger-solid" : "primary"}
            onClick={() => close(true)}
            testId="ds-confirm-confirm"
          >
            {state.confirmLabel}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {dialog}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used inside <ConfirmProvider>");
  return ctx;
}

/** JSX-based alternative for cases where the imperative API is inconvenient. */
export function ConfirmDialog({ open, onOpenChange, onConfirm, ...opts }) {
  const confirm = useConfirm();
  React.useEffect(() => {
    if (!open) return;
    confirm(opts).then(ok => {
      onOpenChange?.(false);
      if (ok) onConfirm?.();
    });
  }, [open]);
  return null;
}
